import { useState, useEffect, useCallback, createContext, useContext, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { PRODUCT_TO_TIER } from "@/constants/stripeTiers";
import { logEdgeFunctionError } from "@/utils/sentry";

const REFRESH_INTERVAL_MS = 3_600_000; // 1 hour

interface SubscriptionState {
  subscribed: boolean;
  tier: string | null;
  productId: string | null;
  subscriptionEnd: string | null;
  cancelAtPeriodEnd: boolean;
  isTrial: boolean;
  trialEnd: string | null;
  subscriptionTrialEnd: string | null;
  subscriptionCancelledAt: string | null;
  subscriptionStatus: string | null;
  subscriptionGracePeriodEnd: string | null;
  /** Authoritative: backend mirrors create-checkout's Stripe-side check. */
  eligibleForTrial: boolean;
  loading: boolean;
  refresh: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionState>({
  subscribed: false,
  tier: null,
  productId: null,
  subscriptionEnd: null,
  cancelAtPeriodEnd: false,
  isTrial: false,
  trialEnd: null,
  subscriptionTrialEnd: null,
  subscriptionCancelledAt: null,
  subscriptionStatus: null,
  subscriptionGracePeriodEnd: null,
  eligibleForTrial: true,
  loading: true,
  refresh: async () => {},
});

export const useSubscription = () => useContext(SubscriptionContext);

interface SubscriptionProviderProps {
  children: ReactNode;
}

export const SubscriptionProvider = ({ children }: SubscriptionProviderProps) => {
  const { user, session, loading: authLoading } = useAuth();
  const [subscribed, setSubscribed] = useState(false);
  const [tier, setTier] = useState<string | null>(null);
  const [productId, setProductId] = useState<string | null>(null);
  const [subscriptionEnd, setSubscriptionEnd] = useState<string | null>(null);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [isTrial, setIsTrial] = useState(false);
  const [trialEnd, setTrialEnd] = useState<string | null>(null);
  const [subscriptionTrialEnd, setSubscriptionTrialEnd] = useState<string | null>(null);
  const [subscriptionCancelledAt, setSubscriptionCancelledAt] = useState<string | null>(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState<string | null>(null);
  const [subscriptionGracePeriodEnd, setSubscriptionGracePeriodEnd] = useState<string | null>(null);
  const [eligibleForTrial, setEligibleForTrial] = useState(true);
  const [loading, setLoading] = useState(true);

  const checkSubscription = useCallback(async () => {
    // Don't resolve as "no subscription" while auth is still loading
    if (!session?.access_token) {
      if (!authLoading) {
        setSubscribed(false);
        setTier(null);
        setProductId(null);
        setSubscriptionEnd(null);
        setCancelAtPeriodEnd(false);
        setIsTrial(false);
        setTrialEnd(null);
        setSubscriptionTrialEnd(null);
        setSubscriptionCancelledAt(null);
        setSubscriptionStatus(null);
        setSubscriptionGracePeriodEnd(null);
        setEligibleForTrial(true);
        setLoading(false);
      }
      return;
    }

    try {
      const [edgeResult, profileResult] = await Promise.all([
        supabase.functions.invoke("check-subscription"),
        supabase
          .from("user_profiles")
          .select(
            "subscription_trial_end, subscription_cancelled_at, subscription_status, subscription_grace_period_end"
          )
          .eq("user_id", user?.id ?? "")
          .maybeSingle(),
      ]);

      if (edgeResult.error) throw edgeResult.error;

      const data = edgeResult.data;
      setSubscribed(data?.subscribed ?? false);
      setSubscriptionEnd(data?.subscription_end ?? null);
      setCancelAtPeriodEnd(data?.cancel_at_period_end ?? false);
      setIsTrial(data?.is_trial ?? false);
      setTrialEnd(data?.trial_end ?? null);
      setProductId(data?.product_id ?? null);
      setEligibleForTrial(data?.eligible_for_trial ?? true);
      setTier(
        data?.product_id ? (PRODUCT_TO_TIER[data.product_id] ?? data.product_id) : null
      );

      // user_profiles read populates the source-of-truth trial/cancellation fields.
      // Failure here is non-fatal — the edge function already returned the access decision.
      const profile = profileResult.data;
      if (!profileResult.error) {
        setSubscriptionTrialEnd(profile?.subscription_trial_end ?? null);
        setSubscriptionCancelledAt(profile?.subscription_cancelled_at ?? null);
        setSubscriptionStatus(profile?.subscription_status ?? null);
        setSubscriptionGracePeriodEnd(profile?.subscription_grace_period_end ?? null);
      }
    } catch (err) {
      logEdgeFunctionError('check-subscription', err);
      // Keep previous state on error
    } finally {
      setLoading(false);
    }
  }, [session?.access_token, authLoading, user?.id]);

  // Check on login / session change
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Periodic refresh
  useEffect(() => {
    if (!user) return;
    const interval = setInterval(checkSubscription, REFRESH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  return (
    <SubscriptionContext.Provider
      value={{
        subscribed,
        tier,
        productId,
        subscriptionEnd,
        cancelAtPeriodEnd,
        isTrial,
        trialEnd,
        subscriptionTrialEnd,
        subscriptionCancelledAt,
        subscriptionStatus,
        subscriptionGracePeriodEnd,
        eligibleForTrial,
        loading,
        refresh: checkSubscription,
      }}
    >
      {children}
    </SubscriptionContext.Provider>
  );
};
